'use client'

import { RefObject, useEffect, useRef, useState } from 'react'

type UseVideoAutoplayParams = {
  videoRef: RefObject<HTMLVideoElement | null>
  /**
   * When false the hook is a no-op (e.g. mobile with no mobile video asset).
   * The image remains as the only visible layer.
   */
  enabled: boolean
  /**
   * IntersectionObserver rootMargin. Defaults to '0px' so the video only
   * starts loading when the hero is actually on screen.
   */
  rootMargin?: string
}

type UseVideoAutoplayReturn = {
  /** Attach to the wrapper element that the IntersectionObserver watches. */
  containerRef: RefObject<HTMLDivElement | null>
  /**
   * Becomes true after the first successful play(). Used to fade out the
   * poster image once the video is running.
   */
  isVideoPlaying: boolean
}

/**
 * Manages the image-first, video-second autoplay lifecycle:
 *
 * 1. Watches the container with IntersectionObserver.
 * 2. On first intersection: checks prefers-reduced-motion and saveData,
 *    then defers video src assignment to a browser idle callback so the
 *    main thread isn't blocked during hydration.
 * 3. Calls video.play() and sets isVideoPlaying on success.
 * 4. Pauses the video when it leaves the viewport to avoid wasted GPU/CPU.
 */
export function useVideoAutoplay({
  videoRef,
  enabled,
  rootMargin = '0px',
}: UseVideoAutoplayParams): UseVideoAutoplayReturn {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  // Track whether the src has been set so we don't re-assign on re-intersection.
  const srcAssigned = useRef(false)

  useEffect(() => {
    if (!enabled) return

    const video = videoRef.current
    const container = containerRef.current
    if (!video || !container) return

    // Respect user OS-level accessibility and network preferences.
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    // navigator.connection is not in all TS lib types; cast via unknown.
    const saveData =
      (navigator as unknown as { connection?: { saveData?: boolean } })
        .connection?.saveData === true

    if (prefersReducedMotion || saveData) return

    let idleCallbackId: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const startVideo = () => {
      // Guard: src must have been assigned before we can play.
      if (!video.src) return
      video
        .play()
        .then(() => setIsVideoPlaying(true))
        // Autoplay was blocked (browser policy / power save). Image stays visible.
        .catch(() => {})
    }

    const assignSrcAndPlay = () => {
      if (srcAssigned.current) {
        // src already set — just try playing (re-entered viewport).
        startVideo()
        return
      }

      // The data-src attribute is set by HeroBannerVideoSlide before this hook runs.
      const pendingSrc = video.dataset.src
      if (!pendingSrc) return

      srcAssigned.current = true
      video.src = pendingSrc
      // Remove the data attribute to keep the DOM clean.
      delete video.dataset.src

      video.load()
      video.addEventListener('canplay', startVideo, { once: true })
    }

    // Defer src assignment to an idle frame so the main thread isn't blocked
    // during or immediately after hydration. 2 s timeout ensures it still runs
    // on slow devices where idle callbacks are delayed.
    const scheduleAssignment = () => {
      if ('requestIdleCallback' in window) {
        idleCallbackId = window.requestIdleCallback(assignSrcAndPlay, {
          timeout: 2000,
        })
      } else {
        timeoutId = setTimeout(assignSrcAndPlay, 200)
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          scheduleAssignment()
        } else if (srcAssigned.current && !video.paused) {
          // Pause when scrolled out of view to save resources.
          video.pause()
        }
      },
      { rootMargin, threshold: 0 }
    )

    observer.observe(container)

    return () => {
      observer.disconnect()
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId)
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
      video.removeEventListener('canplay', startVideo)
    }
  }, [enabled, rootMargin, videoRef])

  return { containerRef, isVideoPlaying }
}
